/* eslint-disable max-len */
import { useDispatch, useSelector } from 'react-redux';
import { reviewClaim } from '@api/review-claim';
import { setReviewInfo, setSelectedData } from '@store';
import { ClaimType, TOTAL_MONEY_LIMIT_FOR_CHECKED } from '@constants';
import { logError } from '@log';

// 获取单选 package 的数据
const getDelayReportedItems = ({
  fulfilledItems,
  delayPackageId,
}) => {
  const packageItem = fulfilledItems.find((packageItem) => packageItem.fulfillmentId === delayPackageId) || {};
  const { items = [] } = packageItem;
  const itemList = [];
  items.forEach((item) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < item.quantity; i++) {
      itemList.push(item);
    }
  });
  return [{
    fulfillmentId: packageItem.fulfillmentId,
    fulfillmentName: packageItem.fulfillmentName,
    items: itemList,
    packageIndex: packageItem.packageIndex,
  }];
};

const getCheckedReportedItems = ({
  fulfilledItems, selectedItems,
}) => {
  const result = [];
  // console.log(fulfilledItems, 'fulfilledItems ');

  fulfilledItems.forEach((packageItem, pIndex) => {
    const { items } = packageItem;
    const ids = selectedItems.filter((item) => {
      // eslint-disable-next-line no-unused-vars
      const [lineItemId, packageIndex, lineIndex] = item.split('__');
      return +packageIndex === pIndex;
    });
    if (!ids?.length) {
      return;
    }
    const itemList = [];
    items.forEach((item, index) => {
      ids.forEach((id) => {
        // eslint-disable-next-line no-unused-vars
        const [lineItemId, packageIndex, lineIndex] = id.split('__');
        if (lineItemId === item.lineItemId && index === +lineIndex) {
          itemList.push(item);
        }
      });
    });
    result.push({ ...packageItem, items: [...itemList] });
  });
  // console.log(result, 'result');
  return result;
};

export const getFormatPackageList = ({
  delayPackageId,
  type,
  selectedItems, fulfilledItems,
}) => {
  const PackageListMap = {
    [ClaimType.Delay]: () => getDelayReportedItems({ fulfilledItems, delayPackageId }),
    [ClaimType.Theft]: () => getDelayReportedItems({ fulfilledItems, delayPackageId }),
    [ClaimType.Damage]: () => getCheckedReportedItems({ fulfilledItems, selectedItems }),
    [ClaimType.Loss]: () => getCheckedReportedItems({ fulfilledItems, selectedItems }),
    [ClaimType.RA]: () => getCheckedReportedItems({ fulfilledItems, selectedItems }),
    [ClaimType.EW]: () => getCheckedReportedItems({ fulfilledItems, selectedItems }),
    [ClaimType.AD]: () => getCheckedReportedItems({ fulfilledItems, selectedItems }),
  };

  return PackageListMap[type]();
};

// 获取请求中的 itemList 数组
const getItemList = (selectedPackageList, type) => {
  const itemList = [];
  selectedPackageList.forEach((packageItem) => {
    const { items, fulfillmentId } = packageItem;
    if ([ClaimType.Delay, ClaimType.Theft].includes(type)) {
      itemList.push({
        fulfillmentId,
      });
    } else {
      items.forEach((item) => {
        // const { lineItemId } = item;
        // const lastItemIndex = itemList.findIndex((v) => lineItemId === v.lineItemId);
        // if (lastItemIndex === -1) {
        itemList.push({
          comment: item.comment,
          lineItemId: item.lineItemId,
          assetId: item.assetId,
          reason: item.reason,
          fulfillmentId,
          attachments: item?.attachments?.map((file) => ({ fileLink: file.path, originalFileName: file.originalFileName })),

        });
        // } else {
        //   const lastItem = itemList[lastItemIndex];
        //   // eslint-disable-next-line no-plusplus
        //   lastItem.quantity++;
        // }
      });
    }
  });
  return itemList;
};
/**
 * 计算所选items的总金额
 * @param {Array} list  package list
 * @returns {Number} 计算后的总金额
 */
const getTotal = (list) => {
  let total = 0;
  list.forEach((p) => {
    p.items.forEach((item) => {
      const value = item.actualPrice?.match(/[\d.]+/g)[0];
      total += +value;
    });
  });
  return total;
};

const formatDhgateItems = (selectedDhgateItems, type, delayPackageId) => {
  if (type === ClaimType.Delay) {
    return [{ fulfillmentId: delayPackageId }];
  }
  const items = Object.values(selectedDhgateItems);
  const formatData = items.map((item) => {
    const res = { ...item };
    delete res.files1;
    delete res.files2;
    delete res.files3;
    return res;
  });
  return formatData;
};

export const useReviewData = () => {
  const {
    delayPackageId,
    type, liabilityId,
    selectedItems, policyId, fulfilledItems, paymentMethod, paymentInfo, files, comments, selectedDhgateItems, gptDimensionInfo,
  } = useSelector((state) => state.claim);
  const { signature, orderNumber } = useSelector((state) => state.policy);
  // console.log(useSelector((state) => state));
  const dispatch = useDispatch();

  /**
   * 请求review 接口
   * @param {Object} param returnMethod-是否使用 seel shipping  includeBp是否勾选bp insurance
   * @returns null
   */
  const getReviewData = async ({
    payment = {}, useShippingLabel = true, includeBp, packInfo = {}, ...currentPaymentInfo
  } = {}) => {
    const selectedPackageList = getFormatPackageList({
      delayPackageId,
      type,
      selectedItems,
      fulfilledItems,
    });
    const totalMoneyValue = getTotal(selectedPackageList);
    const info = type === ClaimType.RA ? paymentInfo : currentPaymentInfo;
    // eslint-disable-next-line no-nested-ternary
    const packageInfo = Object.keys(packInfo)?.length ? packInfo : Object.keys(gptDimensionInfo)?.length ? gptDimensionInfo : undefined;
    const body = {
      issueType: type,
      attachments: files.map((file) => ({ fileLink: file.path, originalFileName: file.name })),
      items: getItemList(selectedPackageList, type),
      policyId,
      paymentMethod: payment.value || paymentMethod.value, // paymentMethod
      useShippingLabel, // whether use seel's shipping
      ...info,
      comment: comments,
      includeBp: includeBp ?? totalMoneyValue > TOTAL_MONEY_LIMIT_FOR_CHECKED, // whether bp is checked
      packInfo: packageInfo,
    };
    try {
      const data = await reviewClaim({ body });
      if (!data) {
        return false;
      }
      dispatch(setSelectedData({ body, selectedPackageList, totalMoneyValue }));
      dispatch(setReviewInfo({ ...data }));
      return true;
    } catch (error) {
      logError(error);
      return false;
    }
  };

  const getAllTotalMoney = () => {
    const selectedPackageList = getFormatPackageList({
      delayPackageId,
      type,
      selectedItems,
      fulfilledItems,
    });
    const totalMoneyValue = getTotal(selectedPackageList);
    return totalMoneyValue;
  };

  const getDhgateReviewData = async ({ payment = {}, ...info }) => {
    const body = {
      liabilityCode: type,
      liabilityId,
      attachments: files.map((file) => ({ fileLink: file.path, originalFileName: file.name })),
      items: formatDhgateItems(selectedDhgateItems, type, delayPackageId),
      policyId,
      paymentMethod: payment.value || paymentMethod.value, // paymentMethod
      ...info,
      comment: comments,
    };
    try {
      const data = await reviewClaim({ body, path: '/gateway/claim-service/api/resolution-center/dhgate/', headers: { signature, 'order-id': orderNumber } });
      if (!data) {
        return false;
      }
      dispatch(setSelectedData({ body }));
      dispatch(setReviewInfo({ ...data }));
      return true;
    } catch (error) {
      logError(error);
      return false;
    }
  };
  return {
    getReviewData, getAllTotalMoney, getDhgateReviewData,
  };
};
